<!--
  Public Landing Page Template
  ===========================
  
  Main entry point template for the MacSafety E-Forms application. Displays
  different content based on user authentication status, serving as both a
  public welcome page and a redirect destination for authenticated users.
  
  Layout Structure:
  - Header section with logo icon and application branding
  - Content section with conditional authentication-based UI
  - Footer section with copyright information
  - Responsive grid layout with centered content positioning
  
  Authentication States:
  - Unauthenticated: Welcome message with login button
  - Authenticated: Welcome back message with navigation options
  - Admin Users: Additional access to form entries management
  
  
  Conditional Rendering:
  - *ngIf="!isLoggedIn()": Unauthenticated user interface
  - *ngIf="isLoggedIn()": Authenticated user interface
  - *ngIf="isAdmin()": Admin-only navigation options
  
  @author MacSafety Development Team
  @version 1.0
-->

<ion-content class="landing-page">
  <!-- 
    Main Landing Container
    ======================
    Responsive grid layout that centers content and adapts to different screen sizes
    Provides consistent spacing and structure across all sections
  -->
  <ion-grid class="landing-container">
    
    <!-- 
      Header Section
      ==============
      Application branding and title display
      Features the MacSafety logo icon, main title, and subtitle
      Always visible regardless of authentication status
    -->
    <ion-row class="header-section">
      <ion-col size="12" class="ion-text-center">
        <!-- 
          Application Logo Icon
          =====================
          Shield checkmark icon representing safety and security
          Visual identifier for the MacSafety brand
        -->
        <ion-icon name="shield-checkmark-outline" class="logo-icon"></ion-icon>
        
        <!-- 
          Main Application Title
          =======================
          Primary branding text displayed prominently
          Establishes the application identity
        -->
        <h1 class="main-title">MacSafety</h1>
        
        <!-- 
          Application Subtitle
          =====================
          Secondary branding text with lighter color
          Clarifies the application's purpose (E-Forms)
        -->
        <ion-text color="light">
          <p class="subtitle">MacSafety E-Forms</p>
        </ion-text>
      </ion-col>
    </ion-row>

    <!-- 
      Main Content Section
      ====================
      Conditional content area that adapts based on user authentication status
      Responsive column sizing for optimal layout across different screen sizes
    -->
    <ion-row class="content-section">
      <!-- 
        Content Column
        ==============
        Responsive sizing:
        - size="12": Full width on mobile devices
        - size-md="8": 8/12 width on medium screens (tablets)
        - size-lg="6": 6/12 width on large screens (desktops)
        Centers content on larger screens while maintaining full width on mobile
      -->
      <ion-col size="12" size-md="8" size-lg="6" class="ion-text-center">
        
        <!-- 
          Unauthenticated User Interface
          ===============================
          Welcome card displayed when user is not logged in
          Features welcome message and login button
          Triggers Microsoft Entra ID authentication flow
        -->
        <ion-card *ngIf="!isLoggedIn()" class="welcome-card">
          <ion-card-header>
            <ion-card-title>Welcome to MacSafety E-Forms</ion-card-title>
          </ion-card-header>
          <ion-card-content>
            <!-- 
              Welcome Message
              ================
              Instructional text explaining the need to log in
              Uses medium color for secondary information
            -->
            <ion-text color="medium">
              <p>Please log in to access the forms.</p>
            </ion-text>
            
            <!-- 
              Login Button
              =============
              Primary action button for initiating authentication
              Large size for prominent display and easy interaction
              Includes login icon for visual clarity
            -->
            <ion-button 
              expand="block" 
              size="large" 
              (click)="login()"
              class="login-button">
              <ion-icon slot="start" name="log-in-outline"></ion-icon>
              Sign In
            </ion-button>
          </ion-card-content>
        </ion-card>

        <!-- 
          Authenticated User Interface
          =============================
          Welcome back card displayed when user is logged in
          Features personalized greeting and navigation options
          Adapts content based on user permissions (admin vs regular)
        -->
        <ion-card *ngIf="isLoggedIn()" class="welcome-card">
          <ion-card-header>
            <!-- 
              Personalized Welcome Title
              ===========================
              Greets user by name for personalized experience
              Indicates successful authentication status
            -->
            <ion-card-title>Welcome back, {{ userName() }}!</ion-card-title>
          </ion-card-header>
          <ion-card-content>
            <!-- 
              Status Confirmation
              ====================
              Confirms user's authenticated status
              Uses medium color for informational text
            -->
            <ion-text color="medium">
              <p>You are logged in and can access the MacSafety E-Forms.</p>
            </ion-text>
            
            <!-- 
              Navigation Actions List
              =========================
              List of available actions for authenticated users
              Each item provides navigation to different application areas
              Admin users see additional management options
            -->
            <ion-list class="action-buttons">
              <!-- 
                Submit Forms Navigation
                =========================
                Primary action for all authenticated users
                Routes to forms landing page for form submission
                Uses document icon and primary color for emphasis
              -->
              <ion-item button (click)="goToForms()" class="action-item">
                <ion-icon slot="start" name="document-text-outline" color="primary"></ion-icon>
                <ion-label>
                  <h2>Submit Forms</h2>
                  <p>Submit safety forms</p>
                </ion-label>
                <ion-icon slot="end" name="chevron-forward-outline" color="medium"></ion-icon>
              </ion-item>
              
              <!-- 
                View Form Entries Navigation (Admin Only)
                ===========================================
                Secondary action available only to admin users
                Routes to form entries management interface
                Uses list icon and secondary color for distinction
                Conditionally rendered based on admin status
              -->
              <ion-item *ngIf="isAdmin()" button (click)="goToFormEntries()" class="action-item"> 
                <ion-icon slot="start" name="list-outline" color="secondary"></ion-icon>
                <ion-label>
                  <h2>View Form Entries</h2>
                  <p>Browse and manage submitted forms</p>
                </ion-label>
                <ion-icon slot="end" name="chevron-forward-outline" color="medium"></ion-icon>
              </ion-item>
            </ion-list>
          </ion-card-content>
        </ion-card>
      </ion-col>
    </ion-row>

    <!-- 
      Footer Section
      ==============
      Copyright information and legal notices
      Always visible at the bottom of the landing page
      Uses light color for subtle display
    -->
    <ion-row class="footer-section">
      <ion-col size="12" class="ion-text-center">
        <ion-text color="light">
          <p>&copy; 2025 MacSafety E Forms. All rights reserved.</p>
        </ion-text>
      </ion-col>
    </ion-row>
  </ion-grid>
</ion-content> 